import { Flex } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { InvestmentSummaryCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentSummaryCard/InvestmentSummaryCard.tsx';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { ProfileDataRequirementsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/ProfileDataRequirementsDrawer/ProfileDataRequirementsDrawer.tsx';
import {
	approveInvestmentWorkflow,
	makeDraftInvestmentWorkflow,
	startInvestmentWorkflow,
} from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { InvestorProfileCard } from './components/InvestorProfileCard/InvestorProfileCard.tsx';
import { InvestmentPaymentCard } from './components/InvestmentPaymentCard/InvestmentPaymentCard.tsx';
import { InvestmentVehicleCard } from './components/InvestmentVehicleCard/InvestmentVehicleCard.tsx';
import { SidebarSkeleton } from './SidebarSkeleton.tsx';
import { useInvestmentWorkflowInvestorData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/InvestmentWorkflowInvestorDataDrawer/useInvestmentWorkflowInvestorData.ts';
import { useReleaseInvestmentWorkflowMutation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useReleaseInvestmentWorkflowMutation.ts';
import { getFullName } from '@dealstack/frontend/shared/components/UserSidebar/UserSidebar.utils.ts';
import { useNavigateToKycPage } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useNavigateToKycPage.ts';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { buildTableFiltersSearch } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState.ts';
import {
	PAYMENT_HIGHLIGHT_DURATION_MS,
	SidebarPaymentCardAnchor,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/InvestmentDetails.styles.tsx';

const PAYMENT_CARD_ANCHOR_ID = 'investment-details-payment-card';

export const InvestmentDetailsSidebar = () => {
	const tMipManage = useTranslationMipManage();
	const { dealId, mipId, investmentId } = useRequiredParams('dealId', 'mipId', 'investmentId');
	const { data: investmentDetailsData, refetch: refetchSummary } = useInvestmentDetailsSummary();
	const [profileDrawerOpened, setProfileDrawerOpened] = useState(false);
	const [isPaymentCardHighlighted, setIsPaymentCardHighlighted] = useState(false);
	const paymentHighlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const paymentHighlightRafRef = useRef<number | null>(null);
	const navigate = useNavigate();
	const { navigateToKycPage } = useNavigateToKycPage();

	useEffect(
		() => () => {
			if (paymentHighlightTimeoutRef.current) {
				clearTimeout(paymentHighlightTimeoutRef.current);
			}
			if (paymentHighlightRafRef.current !== null) {
				cancelAnimationFrame(paymentHighlightRafRef.current);
			}
		},
		[]
	);

	const startInvestmentMutation = useMutation({
		mutationFn: startInvestmentWorkflow,
		onSuccess: () => {
			void refetchSummary();
			showToastV2({
				title: tMipManage('investment_summary_card.start_investment.success'),
				state: 'success',
			});
		},
		onError: () => {
			showToastV2({
				title: tMipManage('investment_summary_card.start_investment.error'),
				state: 'error',
			});
		},
	});

	const approveMutation = useMutation({
		mutationFn: approveInvestmentWorkflow,
		onSuccess: () => {
			void refetchSummary();
			showToastV2({
				title: tMipManage('investment_summary_card.approve.success'),
				state: 'success',
			});
		},
		onError: () => {
			showToastV2({
				title: tMipManage('investment_summary_card.approve.error'),
				state: 'error',
			});
		},
	});

	const makeDraftMutation = useMutation({
		mutationFn: makeDraftInvestmentWorkflow,
		onSuccess: () => {
			void refetchSummary();
			showToastV2({
				title: tMipManage('investment_summary_card.make_draft.success'),
				state: 'success',
			});
		},
		onError: () => {
			showToastV2({
				title: tMipManage('investment_summary_card.make_draft.error'),
				state: 'error',
			});
		},
	});

	const kycWorkflowId = investmentDetailsData?.kycWorkflowId ?? undefined;
	const { individualSettings } = useInvestmentWorkflowInvestorData(kycWorkflowId);

	const releaseMutation = useReleaseInvestmentWorkflowMutation(mipId);

	const releaseInvestment = () => {
		if (!investmentDetailsData?.investor.id) {
			return;
		}
		releaseMutation.mutate(investmentDetailsData.investor.id, {
			onSuccess: () => {
				void refetchSummary();
			},
		});
	};

	const investorId = investmentDetailsData?.investor.id;
	const pendingActions = investmentDetailsData?.pendingActions;

	const reviewKyc = () => {
		if (!investorId) {
			return;
		}
		navigateToKycPage({ investorId, kycWorkflowId });
	};

	const reviewDocuments = () => {
		const singleDocumentId = pendingActions?.documents.singleDocumentId;

		if (singleDocumentId) {
			navigate(
				mipManagePath(MIP_MANAGE_ROUTES.DOCUMENT_DETAILS, {
					dealId,
					mipId,
					documentWorkflowInstanceId: singleDocumentId,
				})
			);
			return;
		}

		const path = mipManagePath(MIP_MANAGE_ROUTES.DOCUMENTS, { dealId, mipId });
		const search = buildTableFiltersSearch([
			{ id: 'investmentWorkflowId', value: investmentId },
		]);
		navigate({ pathname: path, search });
	};

	const reviewConsideration = () => {
		document.getElementById(PAYMENT_CARD_ANCHOR_ID)?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
		// Restart the pulse even when the card is already in view, so the click
		// is always acknowledged visually.
		setIsPaymentCardHighlighted(false);
		if (paymentHighlightRafRef.current !== null) {
			cancelAnimationFrame(paymentHighlightRafRef.current);
		}
		paymentHighlightRafRef.current = requestAnimationFrame(() => {
			paymentHighlightRafRef.current = null;
			setIsPaymentCardHighlighted(true);
		});

		if (paymentHighlightTimeoutRef.current) {
			clearTimeout(paymentHighlightTimeoutRef.current);
		}

		paymentHighlightTimeoutRef.current = setTimeout(() => {
			setIsPaymentCardHighlighted(false);
		}, PAYMENT_HIGHLIGHT_DURATION_MS);
	};

	if (!investmentDetailsData) {
		return <SidebarSkeleton />;
	}

	const creator = investmentDetailsData?.createdBy;
	const createdBy = getFullName(creator?.firstName, creator?.lastName) || null;
	const canMutate = !investmentDetailsData.isReleased;

	return (
		<Flex direction="column" gap="1rem">
			<InvestmentSummaryCard
				title={investmentDetailsData.programmeName ?? ''}
				investmentStatus={investmentDetailsData.investmentStatus}
				createdOnDate={investmentDetailsData.createdAt}
				releaseDate={investmentDetailsData.actualCompletionDate}
				createdBy={createdBy}
				onStartInvestment={() => startInvestmentMutation.mutate({ mipId, investmentId })}
				isStartInvestmentPending={startInvestmentMutation.isPending}
				onApprove={() => approveMutation.mutate({ mipId, investmentId })}
				isApprovePending={approveMutation.isPending}
				onMakeDraft={() => makeDraftMutation.mutate({ mipId, investmentId })}
				isMakeDraftPending={makeDraftMutation.isPending}
				onRelease={releaseInvestment}
				isReleasePending={releaseMutation.isPending}
				targetStartDate={investmentDetailsData.targetStartDate}
				targetCompletionDate={investmentDetailsData.targetCompletionDate}
				pendingActions={investmentDetailsData.pendingActions}
				onReviewKyc={reviewKyc}
				onReviewDocuments={reviewDocuments}
				onReviewConsideration={reviewConsideration}
			/>

			<InvestorProfileCard
				investorId={investmentDetailsData.investor.id}
				investorName={investmentDetailsData.investor.displayName}
				lastLoginAt={investmentDetailsData.lastLoginAt}
				email={investmentDetailsData.investor.email}
				phone={investmentDetailsData.investor.phoneNumber}
				placeOfBirth={investmentDetailsData.investor.placeOfBirth}
				countryOfBirth={investmentDetailsData.investor.countryOfBirth}
				isPlaceOfBirthRequired={individualSettings.placeOfBirth.checked}
				isCountryOfBirthRequired={individualSettings.countryOfBirth.checked}
				nationality={investmentDetailsData.investor.nationality}
				domicile={investmentDetailsData.investor.domicile}
				taxResidences={investmentDetailsData.investor.taxResidences}
				onInvestorProfileClick={() => setProfileDrawerOpened(true)}
				canMutate={canMutate}
			/>

			<SidebarPaymentCardAnchor
				id={PAYMENT_CARD_ANCHOR_ID}
				$highlighted={isPaymentCardHighlighted}
			>
				<InvestmentPaymentCard
					investmentWorkflowId={investmentDetailsData.investmentWorkflowId}
					cost={investmentDetailsData.payment.cost}
					currency={investmentDetailsData.currency}
					status={investmentDetailsData.payment.status}
					annotation={investmentDetailsData.payment.annotation}
					loans={investmentDetailsData.payment.loans}
					isReleased={investmentDetailsData.isReleased}
				/>
			</SidebarPaymentCardAnchor>

			<InvestmentVehicleCard piv={investmentDetailsData.piv} canMutate={canMutate} />

			<ProfileDataRequirementsDrawer
				opened={profileDrawerOpened}
				onClose={() => setProfileDrawerOpened(false)}
				kycWorkflowId={kycWorkflowId}
			/>
		</Flex>
	);
};
